import { Box, Chip, Stack, Typography, styled } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";
import { fetchSubscribedProductsMock } from "../../data";

const Grid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: theme.spacing(2),
  [theme.breakpoints.up("sm")]: { gridTemplateColumns: "1fr 1fr" },
  [theme.breakpoints.up("lg")]: { gridTemplateColumns: "1fr 1fr 1fr 1fr" },
}));

const ProductCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "status",
})(({ status }) => ({
  borderRadius: "999px",
  fontWeight: 500,
  fontSize: "0.75rem",
  height: 24,
  backgroundColor:
    status === "active"
      ? "#f0fdf4"
      : status === "inactive"
        ? "#fef2f2"
        : "#fffbeb",
  color:
    status === "active"
      ? "#15803d"
      : status === "inactive"
        ? "#dc2626"
        : "#d97706",
}));

const StatBox = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.04)"
      : theme.palette.grey[50],
  borderRadius: 8,
  padding: theme.spacing(1.5),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
}));

const Products = () => {
  const { userId } = useOutletContext() ?? {}; // remove if not using outlet

  const { data, isLoading } = useQuery({
    queryKey: ["subscribed-products", userId],
    queryFn: () => fetchSubscribedProductsMock(userId),
  });

  if (isLoading) {
    return (
      <Grid>
        {Array.from({ length: 4 }).map((_, i) => (
          <ProductCard key={i} sx={{ opacity: 0.5, minHeight: 180 }} />
        ))}
      </Grid>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {data?.total_subscribed ?? 0} subscribed products
      </Typography>

      <Grid>
        {data?.products?.map((product) => (
          <ProductCard key={product.id}>
            {/* header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box>
                <Typography fontWeight={600} fontSize="1rem">
                  {product.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {product.type}
                </Typography>
              </Box>
              <StatusChip
                label={product.status}
                status={product.status}
                size="small"
              />
            </Box>

            {/* stats */}
            <Stack direction="row" spacing={1}>
              <StatBox sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Volume (30d)
                </Typography>
                <Typography fontWeight={600} fontSize="0.95rem">
                  {product.last_30_days.volume_formatted}
                </Typography>
              </StatBox>
              <StatBox sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Count (30d)
                </Typography>
                <Typography fontWeight={600} fontSize="0.95rem">
                  {product.last_30_days.count}
                </Typography>
              </StatBox>
            </Stack>

            {/* subscribed date */}
            <Typography variant="caption" color="text.secondary">
              Subscribed {product.subscribed_date_formatted}
            </Typography>
          </ProductCard>
        ))}
      </Grid>
    </Box>
  );
};

export default Products;
